import { type ClockSchedule, clockDayStart, clockDaysOf, clockOffsetAt } from '@felix/domain';
import { compareByRank } from './crew.js';
import { daySpan, type ShipTime } from './ship-clock.js';
import type { PersonnelSource } from './source.js';
import type {
  ClockChangeView,
  CrewMember,
  CrewRestDay,
  CrewRestReport,
  CrewStatus,
  WorkPeriod,
} from './types.js';

const MINUTE = 60_000;

/**
 * Asgari dinlenme sınırları [MLC A2.3 §5–6; STCW A-VIII/1]; ekrandaki "MLC sınırları" kartı.
 * Hesap sunucuda (`@felix/domain` `REST_LIMITS`); burada yalnız gösterilir.
 */
export const MLC_LIMITS = { minRest24h: 10, maxWork24h: 14, minRest7d: 77, maxWork7d: 72 } as const;

/** Bu süreden kısa ara dinlenme sayılmaz (ADR 0018 §1; sunucudaki varsayılanla aynı). */
export const SHORT_BREAK_MINUTES = 60;

/** Gemi saatinin farkı (dakika): dilimden ya da saat kaydından (ADR 0065 §4). */
export function zoneOffsetMinutes(time: ShipTime, at: number): number {
  if (typeof time !== 'string') return clockOffsetAt(time, at);
  const timeZone = time;
  const part = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' })
    .formatToParts(new Date(at))
    .find((p) => p.type === 'timeZoneName')?.value;
  const match = /GMT([+-])(\d{2}):(\d{2})/.exec(part ?? '');
  if (!match) return 0;
  const minutes = Number(match[2]) * 60 + Number(match[3]);
  return match[1] === '-' ? -minutes : minutes;
}

/** Gemi yerel gününün başladığı an (ms); sunucudaki `localDayStart` ile aynı hesap. */
export function zoneDayStart(time: ShipTime, date: string): number {
  if (typeof time !== 'string') return clockDayStart(time, date);
  const timeZone = time;
  const midnight = Date.parse(`${date}T00:00:00.000Z`);
  const guess = midnight - zoneOffsetMinutes(timeZone, midnight) * MINUTE;
  return midnight - zoneOffsetMinutes(timeZone, guess) * MINUTE;
}

/** Seçilebilir gün: takvimde olan ve bugünden ileri olmayan (gelecek günün kaydı olmaz). */
export function acceptDate(value: string, today: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = Date.parse(`${value}T00:00:00.000Z`);
  if (Number.isNaN(parsed) || new Date(parsed).toISOString().slice(0, 10) !== value) return false;
  return value <= today;
}

export function shiftDate(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00.000Z`) + days * 86_400_000)
    .toISOString()
    .slice(0, 10);
}

/** Günün bandı: dakika cinsinden, gün başından. Dinlenme parçası kısaysa `short`. */
export interface DaySegment {
  kind: 'work' | 'rest';
  from: number;
  to: number;
  activity: string | null;
  location: string | null;
  note: string | null;
  short: boolean;
}

export function daySegments(
  periods: readonly WorkPeriod[],
  time: ShipTime,
  date: string,
): DaySegment[] {
  // Gün sınırları gemi saati kaydından (ADR 0065 §4): 23, 25 ya da tarih çizgisinde 48 saat
  const span = daySpan(time, date);
  const start = span.start;
  const length = span.minutes;
  if (length === 0) return [];
  const work = periods
    .map((p) => ({
      from: Math.max(0, Math.round((Date.parse(p.start) - start) / MINUTE)),
      to: Math.min(length, Math.round((Date.parse(p.end) - start) / MINUTE)),
      period: p,
    }))
    .filter((w) => w.to > w.from)
    .sort((x, y) => x.from - y.from);
  const segments: DaySegment[] = [];
  let cursor = 0;
  const rest = (from: number, to: number) => {
    if (to <= from) return;
    // Gün sınırına dayanan parça kısa sayılmaz: komşu günde devam ediyor olabilir
    const bounded = from > 0 && to < length;
    segments.push({
      kind: 'rest',
      from,
      to,
      activity: null,
      location: null,
      note: null,
      short: bounded && to - from < SHORT_BREAK_MINUTES,
    });
  };
  for (const item of work) {
    rest(cursor, item.from);
    segments.push({
      kind: 'work',
      from: Math.max(cursor, item.from),
      to: item.to,
      activity: item.period.activity,
      location: item.period.location,
      note: item.period.note,
      short: false,
    });
    cursor = Math.max(cursor, item.to);
  }
  rest(cursor, length);
  return segments;
}

/** Ondalık saat → `s:dd` (13.25 → "13:15"). */
export function formatHours(hours: number): string {
  const minutes = Math.round(hours * 60);
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
}

export function formatMinutes(minutes: number): string {
  return formatHours(minutes / 60);
}

/** Dakikadan iki haneli `SS:DD` (555 → "09:15"; iş türü dağılımı, M6f G3-34). */
export function formatClock(minutes: number): string {
  const total = Math.round(minutes);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** "Diğer" iş türü dağılımda her zaman sonda (M6f G3-34). */
const OTHER_ACTIVITY = 'other';

/** İş türü dağılımı: en çok çalışılan önce; "Others" sonda. */
export function activityTotals(
  periods: readonly WorkPeriod[],
): { activity: string; minutes: number; share: number }[] {
  const totals = new Map<string, number>();
  for (const p of periods) {
    const minutes = Math.round((Date.parse(p.end) - Date.parse(p.start)) / MINUTE);
    totals.set(p.activity, (totals.get(p.activity) ?? 0) + minutes);
  }
  const all = [...totals.values()].reduce((sum, m) => sum + m, 0);
  return [...totals.entries()]
    .map(([activity, minutes]) => ({ activity, minutes, share: all ? minutes / all : 0 }))
    .sort(
      (x, y) =>
        Number(x.activity === OTHER_ACTIVITY) - Number(y.activity === OTHER_ACTIVITY) ||
        y.minutes - x.minutes,
    );
}

/**
 * 7 günlük dinlenme çubuğu (M6f G3-35): ölçek dinlenme ya da 77 saat sınırının 1,25 katı (hangisi
 * büyükse); sınır ince bir tik. Yüzde olarak dolgu ve tik konumu.
 */
export function restMeter(rest7d: number | null): { fill: number; mark: number } {
  const rest = rest7d ?? 0;
  const scale = Math.max(rest, MLC_LIMITS.minRest7d * 1.25);
  return { fill: (rest / scale) * 100, mark: (MLC_LIMITS.minRest7d / scale) * 100 };
}

/**
 * Günün toplamları (dakika; M6f G3-10): çalışma ve günün geri kalanı dinlenme. Sunucudaki günlük
 * dinlenme saatiyle aynı hesap: kısa ara (ADR 0018 §1) dinlenme saatinde sayılır, ama dinlenme
 * parçası değildir. Dönemi olmayan gün tümüyle dinlenmedir (24 saat).
 */
export function dayTotals(segments: readonly DaySegment[]): { work: number; rest: number } {
  const length = segments.at(-1)?.to ?? 1440;
  const work = segments
    .filter((segment) => segment.kind === 'work')
    .reduce((sum, segment) => sum + segment.to - segment.from, 0);
  return { work, rest: length - work };
}

const SEVERITY: Readonly<Record<CrewRestDay['status'], number>> = {
  violation: 3,
  warning: 2,
  incomplete: 1,
  compliant: 0,
};

/** Kişinin günü; tarih çizgisinde iki kez yaşanan günde en kötü oluşum (ADR 0065 §4). */
export function dayOf(person: CrewRestReport, date: string): CrewRestDay | undefined {
  return person.days
    .filter((d) => d.date === date)
    .reduce<CrewRestDay | undefined>(
      (worst, day) => (!worst || SEVERITY[day.status] > SEVERITY[worst.status] ? day : worst),
      undefined,
    );
}

export function statusCounts(crew: readonly CrewRestReport[], date: string) {
  const counts = { total: crew.length, compliant: 0, warning: 0, violation: 0, incomplete: 0 };
  for (const person of crew) {
    const day = dayOf(person, date);
    counts[day?.status ?? 'incomplete'] += 1;
  }
  return counts;
}

/**
 * Seçili gemi gününün listesi (ortak notu P1, 2026-11-02; ADR 0016 Ek): 7 günlük raporun
 * kişileri o günün üyeleriyle süzülür. Rapor uyum hesabı için 7 güne değen her atamanın sahibini
 * verir; liste yalnız o gün bu gemide görevli olanları gösterir. `members` `null` ise (üyelik
 * okunamadı) süzgeç yok. `keep` ile verilenler (`?user=` pano uyarısı, düzenlenen kişi) süzgece
 * takılmaz; raporda olmayan kişi yine gelmez.
 */
export function crewOnDay(
  crew: readonly CrewRestReport[],
  members: ReadonlySet<string> | null,
  ...keep: (string | null)[]
): CrewRestReport[] {
  if (!members) return [...crew];
  return crew.filter((person) => members.has(person.userId) || keep.includes(person.userId));
}

/**
 * Seçili gemi gününün üyeleri (ortak notu P1): o gün bu gemide ataması olanlar. Mevcut uçtan tek
 * günlük raporla okunur: `restHours(vesselId, day, day)` o güne değen atamaların sahiplerini verir
 * (katılış ve ayrılış günü dahil; dönem yazmanın `NO_ASSIGNMENT` kuralıyla aynı). Yeni sorgu
 * parametresi yok: eski gemi sunucusunda `rangeQuery` `z.strictObject`'tir, bilinmeyen parametre
 * bütün ekranı kırardı. Okunamazsa `null` (süzgeç yok; yedek yol çağıranda):
 * - çağrı hata verirse;
 * - gemi saati çizelgesi (`clock`) günün tarih çizgisinde atlandığını söylüyorsa çağrı hiç
 *   yapılmaz. Sunucu atlanan günün raporunu çıkaramaz, 500 döner ve hata Sorun kayıtlarına düşer
 *   (ADR 0065 §4, ADR 0149). Çizelge yoksa (eski sunucu) çağrı yapılır.
 */
export async function membersOn(
  source: {
    restHours(
      vesselId: string,
      from: string,
      to: string,
    ): Promise<{ crew: readonly { userId: string }[] }>;
  },
  vesselId: string,
  day: string,
  clock?: ClockSchedule | undefined,
): Promise<ReadonlySet<string> | null> {
  if (clock && clockDaysOf(clock, day).some((d) => d.skipped)) return null;
  try {
    const { crew } = await source.restHours(vesselId, day, day);
    return new Set(crew.map((person) => person.userId));
  } catch {
    return null;
  }
}

/**
 * Kişinin `/crew` kaydına göre bu gemideki durumu (ortak notu P1): durum rozeti ve "On board"
 * noktası. Gemide `/crew` yalnız bu geminin bugünkü kadrosudur; ofiste filo genelidir ve `vessel`
 * kişi gemideyse bulunduğu, karadaysa son ayrıldığı gemidir (ADR 0012 §5). Başka gemide görevli
 * kişi bu geminin listesinde "On board" görünmez (`null`); karada ve atanmamış gemiden bağımsızdır.
 */
export function statusHere(
  member: CrewMember | undefined,
  vesselId: string | null | undefined,
): CrewStatus | null {
  if (!member) return null;
  if (member.status !== 'on_board') return member.status;
  return vesselId && member.vessel?.id === vesselId ? 'on_board' : null;
}

/**
 * Tek günlük üyelik okunamayınca yedek üyelik (ortak notu P1): bugün için `/crew`da bu gemide
 * görevli olanlar; öteki günlerde `null`, süzgeç yok (düzeltmeden önceki davranış). `/crew`
 * yalnız tamsa kullanılır: 7 günlük raporun (`report`) her kişisi `/crew`da olmalı, yoksa `null`.
 * - Ofiste `/crew` filo listesidir (karadakiler de); raporun herkesi oradadır.
 * - Gemide `/crew` yalnız bugün gemide olanlardır. `crew_certificates` gemi kapsamı yoksa yalnız
 *   oturum sahibidir (seed: Chief Officer, Chief Engineer dinlenme saatlerini gemi kapsamında
 *   görür, belgeleri yalnız kendininkini). Raporda olup `/crew`da olmayan kişinin ayrılmış mı,
 *   yetkiyle gizli mi olduğu bilinmez; liste daraltılmaz.
 * Bilinen fark: `/crew` ayrılış gününü görevli saymaz (`crewStatus`, ADR 0131 Ek 4), tek günlük
 * rapor sayar; yedekte bugün ayrılan kişi listede yoktur.
 */
export function onBoardToday(
  crew: readonly CrewMember[],
  report: readonly { userId: string }[],
  vesselId: string,
  day: string,
  today: string,
): ReadonlySet<string> | null {
  if (day !== today) return null;
  const known = new Set(crew.map((member) => member.userId));
  if (report.some((person) => !known.has(person.userId))) return null;
  return new Set(
    crew
      .filter((member) => statusHere(member, vesselId) === 'on_board')
      .map((member) => member.userId),
  );
}

/** Hours of Work & Rest ekranının raporu (`loadRestReport`). */
export interface RestReportData {
  timeZone: string;
  /** 7 günlük rapor (`day-6`–`day`): uyum hesabı, haftalık tablo ve grafik. */
  crew: CrewRestReport[];
  /** Seçili günün üyeleri (`membersOn`); `null` süzgeç yok. */
  members: ReadonlySet<string> | null;
  clockChanges: ClockChangeView[];
}

/**
 * Hours of Work & Rest verisi (ortak notu P1, 2026-11-02): 7 günlük rapor uyum hesabı için aynen;
 * liste üyeliği seçili günden (`membersOn`). İkisi birlikte gelir: yüklenirken liste süzülmemiş
 * eski hâline dönmez. Tek günlük çağrı hata verirse ya da gün tarih çizgisinde atlandıysa (`clock`;
 * çağrı yapılmaz) bugün için `/crew`daki görevliler (yalnız `/crew` tamsa: `onBoardToday`), öteki
 * durumlarda süzgeç yok; 7 günlük rapor hata verirse ekran hata gösterir.
 */
export async function loadRestReport(
  source: Pick<PersonnelSource, 'restHours'>,
  input: {
    vesselId: string;
    day: string;
    today: string;
    crew: readonly CrewMember[];
    /** Gemi saati çizelgesi (`PersonnelVessel.clock`); eski sunucu göndermez. */
    clock?: ClockSchedule | undefined;
  },
): Promise<RestReportData> {
  const { vesselId, day, today, crew, clock } = input;
  const [week, members] = await Promise.all([
    source.restHours(vesselId, shiftDate(day, -6), day),
    membersOn(source, vesselId, day, clock),
  ]);
  return {
    timeZone: week.vessel.timeZone,
    crew: week.crew,
    members: members ?? onBoardToday(crew, week.crew, vesselId, day, today),
    clockChanges: week.clockChanges ?? [],
  };
}

/**
 * Kişi listesi (ortak notu P1): liste, arama, sıralama, üst sayaçlar ve varsayılan kişi yalnız
 * seçili günün görevlilerinden (`crewOnDay`). `?user=` ile gelen kişi (pano uyarısı) listede
 * kalır ve seçilebilir, sayaçlara girmez. Düzenleme penceresi açıkken düzenlenen kişi de kalır:
 * pencerenin gün düğmeleri kişiyi değiştirmez, saat başkasının adına girilmez. Düzenlenen kişi
 * gidilen günün 7 günlük raporunda hiç yoksa (ör. katılıştan önceki gün) kişi `null` olur, asla
 * başka biri değil; ekran pencereyi kapatır. Sıra rütbe kıdemi (M6f G3-17); arama ad ve rütbede.
 * Pencere kapalıyken seçili kişi listede yoksa (gün ya da gemi değişti) ilk görevli.
 */
export function restRoster(input: {
  crew: readonly CrewRestReport[];
  members: ReadonlySet<string> | null;
  day: string;
  /** `?user=` ile gelen kişi (pano uyarısı). */
  focusUser: string | null;
  /** Düzenleme penceresi açıkken düzenlenen kişi. */
  editingUser?: string | null;
  personId: string | null;
  query: string;
  rankOf: (userId: string) => string | null;
  locale: string;
}): {
  visible: CrewRestReport[];
  person: CrewRestReport | null;
  counts: ReturnType<typeof statusCounts>;
} {
  const { crew, members, day, focusUser, personId, query, rankOf, locale } = input;
  const editingUser = input.editingUser ?? null;
  const onDay = crewOnDay(crew, members);
  const ranked = crewOnDay(crew, members, focusUser, editingUser).sort((x, y) =>
    compareByRank(
      { fullName: x.fullName, role: rankOf(x.userId) },
      { fullName: y.fullName, role: rankOf(y.userId) },
      locale,
    ),
  );
  const q = query.trim().toLocaleLowerCase(locale);
  const visible = ranked.filter(
    (p) => !q || `${p.fullName} ${rankOf(p.userId) ?? ''}`.toLocaleLowerCase(locale).includes(q),
  );
  // Pencere açıkken yalnız düzenlenen kişi: raporda yoksa başkasına düşülmez
  const person = editingUser
    ? (ranked.find((p) => p.userId === editingUser) ?? null)
    : (ranked.find((p) => p.userId === personId) ?? ranked.find((p) => onDay.includes(p)) ?? null);
  return { visible, person, counts: statusCounts(onDay, day) };
}

/**
 * Uyum kartının altındaki not. Kaydı eksik gün değerlendirilemez; "sınırlar içinde" denmez
 * (temkinli varsayım: kayıt eksikliği uyum sayılmaz; ADR 0016 §2).
 */
export function complianceNotice(
  status: CrewRestDay['status'] | null,
  findings: number,
): 'findings' | 'incomplete' | 'clear' {
  if (findings > 0) return 'findings';
  if (status === null || status === 'incomplete') return 'incomplete';
  return 'clear';
}
